import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ServicerProvider } from './servicer';

import { LoginApiParameter } from './models/login-api-parameter';

import { GenericItem } from './models/generic-item';
import { SystemAccount } from './models/system-account';
import { SystemSession } from './models/system-session';

import { SystemAccountAuthorityType } from '../../types/system-account-authority-type';

@Injectable()
export class SystemSessionServiceProvider {
  session$: BehaviorSubject<SystemSession> = new BehaviorSubject<SystemSession>(null);

  constructor(
    private servicer: ServicerProvider
  ) { }

  /**
   * セッションを取得する.
   */
  getSession(): SystemSession {
    return this.session$.getValue();
  }

  /**
   * セッションを削除する.
   */
  deleteSession() {
    this.session$.next(null);
  }

  /**
   * セッションを保存する.
   *
   * @param session システムアカウント
   */
  saveSession(session: SystemSession) {
    this.session$.next(session);
  }

  /**
   * ログイン.
   * 指定したログインID・パスワードでログイン認証を実行する。
   *
   * @param parameter LoginApiParameter
   * @returns Observable<GenericItem<SystemAccount>>
   */
  login(parameter: LoginApiParameter): Observable<GenericItem<SystemAccount>> {
    return this.servicer.postApi<LoginApiParameter, GenericItem<SystemAccount>>('/v2/system/login', parameter)
      .pipe(
        tap((res) => {
          this.saveSession(this.toSystemSession(parameter.loginId, res.item));
        }),
      );
  }

  /**
   * システムセッションに変換します.
   *
   * @param loginId ログインID
   * @param systemAccount システムアカウント
   * @returns システムセッション
   */
  private toSystemSession(loginId: string, systemAccount: SystemAccount): SystemSession {
    const systemSession: SystemSession = {
      loginId: loginId,
      authority: systemAccount.authority,
      permission: {
        setting: false,
        systemSetting: false,
        areaSetting: false,
        vehicleSetting: false,
        userSetting: false,
        stopPointSetting: false,
        waitPointPlanSetting: false,
        restPlanSetting: false,
        reservationCancel: false,
        oosChange: false,
        waitPointChange: false,
        chargeDeterrentChange: false,
        chargePlanSetting: false
      }
    };

    this.setPermission(systemSession);

    return systemSession;
  }

  /**
   * 許可する操作を設定します.
   *
   * @param systemSession システムセッション
   */
  private setPermission(systemSession: SystemSession) {
    switch (systemSession.authority) {
      case SystemAccountAuthorityType.ROLE_ADMIN:
        this.setAdminPermission(systemSession);
        break;
      case SystemAccountAuthorityType.ROLE_OPER:
        this.setOperPermission(systemSession);
        break;
      case SystemAccountAuthorityType.ROLE_VIEW:
        this.setViewPermission(systemSession);
        break;
      default:
        // 閲覧権限と同じとする
        this.setViewPermission(systemSession);
    }
  }

  /**
   * 管理権限で許可する操作を設定します.
   *
   * @param systemSession システムセッション
   */
  private setAdminPermission(systemSession: SystemSession) {
    systemSession.permission.setting = true;
    systemSession.permission.systemSetting = true;
    systemSession.permission.areaSetting = true;
    systemSession.permission.vehicleSetting = true;
    systemSession.permission.userSetting = true;
    systemSession.permission.stopPointSetting = true;
    systemSession.permission.waitPointPlanSetting = true;
    systemSession.permission.restPlanSetting = true;
    systemSession.permission.reservationCancel = true;
    systemSession.permission.oosChange = true;
    systemSession.permission.waitPointChange = true;
    systemSession.permission.chargeDeterrentChange = true;
    systemSession.permission.chargePlanSetting = true;
  }

  /**
   * 運用権限で許可する操作を設定します.
   *
   * @param systemSession システムセッション
   */
  private setOperPermission(systemSession: SystemSession) {
    systemSession.permission.setting = true;
    systemSession.permission.userSetting = true;
    systemSession.permission.reservationCancel = true;
    systemSession.permission.oosChange = true;
    systemSession.permission.waitPointChange = true;
    systemSession.permission.chargeDeterrentChange = true;
  }

  /**
   * 閲覧権限で許可する操作を設定します.
   *
   * @param systemSession システムセッション
   */
  private setViewPermission(systemSession: SystemSession) {
    // 許可する操作なし
  }
}



// WEBPACK FOOTER //
// ./src/providers/servicer/system-session-service.ts